import {
  createElement,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import {
  aggregateFunctionLabel,
  aggregateMenuGroupsForColumnType,
  type AggregateFunction,
  type AggregateMenuGroupId,
} from '@/lib/databases/values';

const SUBMENU_MIN_WIDTH = 200;

export const CALCULATE_MENU_MIN_WIDTH = 168;

function anchorSubmenu(
  rowEl: HTMLElement,
  side: 'left' | 'right',
): { left: number; top: number } {
  const rect = rowEl.getBoundingClientRect();
  if (side === 'right') {
    return { left: rect.right + 4, top: rect.top };
  }
  return {
    left: Math.max(8, rect.left - SUBMENU_MIN_WIDTH + 8),
    top: rect.top,
  };
}

function CheckIcon({ visible }: { visible: boolean }) {
  if (!visible) {
    return createElement('span', { className: 'cs-db-calc-check' });
  }
  return createElement(
    'span',
    { className: 'cs-db-calc-check cs-db-calc-check--on', 'aria-hidden': 'true' },
    '✓',
  );
}

function CalculateMenuItem({
  label,
  selected,
  hasSubmenu,
  active,
  buttonRef,
  onPress,
  onHover,
}: {
  label: string;
  selected?: boolean;
  hasSubmenu?: boolean;
  active?: boolean;
  buttonRef?: (el: HTMLButtonElement | null) => void;
  onPress?: () => void;
  onHover?: () => void;
}) {
  return createElement(
    'button',
    {
      ref: buttonRef,
      type: 'button',
      className: `cs-db-calc-item${active ? ' cs-db-calc-item--active' : ''}`,
      onMouseEnter: onHover,
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        onPress?.();
      },
    },
    createElement('span', { className: 'cs-db-calc-item-label' }, label),
    hasSubmenu
      ? createElement('span', { className: 'cs-db-calc-chevron', 'aria-hidden': 'true' }, '›')
      : createElement(CheckIcon, { visible: Boolean(selected) }),
  );
}

function CalculateSubmenu({
  anchorRow,
  groupId,
  functions,
  selectedFn,
  submenuSide,
  onSelect,
  onHoverGroup,
}: {
  anchorRow: HTMLButtonElement | null;
  groupId: AggregateMenuGroupId;
  functions: AggregateFunction[];
  selectedFn: AggregateFunction | undefined;
  submenuSide: 'left' | 'right';
  onSelect: (fn: AggregateFunction) => void;
  onHoverGroup: (id: AggregateMenuGroupId) => void;
}) {
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useLayoutEffect(() => {
    if (!anchorRow) {
      return;
    }
    setPosition(anchorSubmenu(anchorRow, submenuSide));
  }, [anchorRow, submenuSide]);

  if (!anchorRow || typeof document === 'undefined') {
    return null;
  }

  const maxHeight =
    typeof window !== 'undefined'
      ? Math.max(120, window.innerHeight - position.top - 8)
      : undefined;

  return createPortal(
    createElement(
      'div',
      {
        'data-cs-footer-calculate': 'true',
        className:
          'fixed overflow-y-auto overflow-x-hidden rounded-lg border border-border bg-surface py-1 shadow-xl',
        style: {
          left: position.left,
          top: position.top,
          minWidth: SUBMENU_MIN_WIDTH,
          maxHeight,
          zIndex: 100000,
        },
        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
        onMouseEnter: () => onHoverGroup(groupId),
      },
      functions.map((fn) =>
        createElement(CalculateMenuItem, {
          key: fn,
          label: aggregateFunctionLabel(fn),
          selected: selectedFn === fn,
          onPress: () => onSelect(fn),
        }),
      ),
    ),
    document.body,
  );
}

export type CalculateMenuPanelProps = {
  columnType: string;
  selectedFn: AggregateFunction | undefined;
  submenuSide?: 'left' | 'right';
  onSelect: (fn: AggregateFunction | null) => void;
};

/** Notion-style nested Calculate menu (None / Count / Percent / More options). */
export function CalculateMenuPanel({
  columnType,
  selectedFn,
  submenuSide = 'left',
  onSelect,
}: CalculateMenuPanelProps) {
  const [hoverGroup, setHoverGroup] = useState<AggregateMenuGroupId | null>(null);
  const groupRowRefs = useRef<Partial<Record<AggregateMenuGroupId, HTMLButtonElement | null>>>(
    {},
  );

  const groups = aggregateMenuGroupsForColumnType(columnType);
  const activeGroup = groups.find((g) => g.id === hoverGroup) ?? null;

  function selectFn(fn: AggregateFunction | null) {
    onSelect(fn);
    setHoverGroup(null);
  }

  return createElement(
    'div',
    { className: 'cs-db-calc-menu' },
    createElement(CalculateMenuItem, {
      label: 'None',
      selected: !selectedFn,
      onPress: () => selectFn(null),
    }),
    groups.map((group) =>
      createElement(CalculateMenuItem, {
        key: group.id,
        label: group.label,
        hasSubmenu: true,
        active: hoverGroup === group.id,
        buttonRef: (el: HTMLButtonElement | null) => {
          groupRowRefs.current[group.id] = el;
        },
        onHover: () => setHoverGroup(group.id),
      }),
    ),
    activeGroup
      ? createElement(CalculateSubmenu, {
          anchorRow: groupRowRefs.current[activeGroup.id] ?? null,
          groupId: activeGroup.id,
          functions: activeGroup.functions,
          selectedFn: selectedFn,
          submenuSide,
          onSelect: (fn) => selectFn(fn),
          onHoverGroup: setHoverGroup,
        })
      : null,
  );
}

export function hasFooterAggregates(
  columnAggregates: Record<string, AggregateFunction> | undefined,
): boolean {
  return columnAggregates != null && Object.keys(columnAggregates).length > 0;
}
